import styled from 'styled-components';
import { ConsolePanel } from '../ConsolePanel';
import { surfaceGradient } from '@/theme/surfaceGradient';

export interface PanelProps {
  /** Inherited from ConsolePanel — raises the (focus/active) glow. */
  $elevated?: boolean;
  /**
   * RETIRED in 1.25.00 and now a no-op. Kept in the type so the ~dozen call
   * sites passing $brackets={false} keep compiling; a follow-up sweep removes
   * the prop from callers.
   *
   * The accent L-brackets were the June teal-HUD signature, repainted purple by
   * the July retheme and never removed. They were sci-fi costume repeated on
   * every post in a scrolling feed — ornament that encoded nothing — and by
   * 1.24.80 RailGroup hid them and AuthCard opted out, so they only ever
   * appeared where nobody had thought about them.
   */
  $brackets?: boolean;
}

/**
 * Panel — the card primitive. Extends ConsolePanel with the card radius and a
 * neutral hairline. Inherits the glass-aware surface (opaque by default;
 * translucent + blur only when the member turns glass cards on). Built as
 * styled(ConsolePanel) so `styled(Panel).attrs({ as: 'article' })` works.
 *
 * Depth here is a lighter surface plus a 1px rule — never a shadow and never a
 * glow. That is what keeps a feed of these reading as a set of sheets rather
 * than a stack of floating template cards.
 */
export const Panel = styled(ConsolePanel)<PanelProps>`
  /* elevation + box-shadow + blur are inherited from ConsolePanel; Panel
     re-asserts the (glass-aware) background since it would otherwise override it. */
  position: relative;
  /* LONGHAND: the background shorthand would wipe ConsolePanel's
     background-image and silently delete the card's grain + sheen layers. */
  background-color: ${({ theme, $surfaceRole }) =>
    theme.cardsGlass
      ? surfaceGradient(theme, $surfaceRole) === 'none'
        ? theme.color.glassBg
        : 'transparent'
      : theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
`;
